import { Auth0CartStorage, CartStorage, LocalCartStorage } from "./store";
import {
  BIG_COMMERCE_CART_ID_COOKIE,
  CHECKOUT_INTENT_PATHNAME_KEY,
  GUEST_CART_EMAIL_COOKIE,
  GUEST_CHECKOUT_PATH,
} from "../../utils/constants";
import CartContext, { CartContextInterface } from "./context";
import { CartResolver, checkInventoryLevel } from "./cart-resolver";
import {
  trackCartCouponEvent,
  trackCheckoutButtonClicked,
  trackOfferDetailsEvent,
} from "../../utils/event-tracking";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { ButtonPosition, Cart } from "./types";
import { VinoDataOffer } from "../../types/vino-data-types";
import config from "../../../config.json";
import contextLogger from "../../utils/context-logger";
import { initialCartState } from "./state";
import { isCouponForLoggedInUser } from "../../utils/is-coupon-for-loggedin-user";
import { navigate } from "gatsby";
import { reducer } from "./reducer";
import useAuth from "../../hooks/use-auth";
import { useLocation } from "@reach/router";
import useOneSignal from "../../hooks/use-onesignal";
import vinoFetch from "../../utils/vinoFetch";
import { EventSubType, EventType } from "../../types";
import { trackEvent } from "../../utils/track-event";
import useScreenSize from "../../hooks/use-screen-size";
import { openToast } from "@vinomofo/components";
import { ErrorToast, SuccessToast } from "../../components/toast";
import axios from "axios";
import { Offer } from "@vinomofo/gatsby-types";

type CartProviderProps = React.PropsWithChildren<{ initialCartId?: string }>;

const MAX_FETCH_STORE_RETRIES = 10;
const LOW_INVENTORY_COUNT =
  config[process.env.GATSBY_VM_MARKET].LOW_INVENTORY_COUNT;

/** Segment should reflect the page the user started checkout from, not /auth-callback/. */
function resolveCheckoutTrackingLocation(pathname: string | undefined): string {
  const p = pathname ?? "";
  if (
    typeof window !== "undefined" &&
    p.includes("auth-callback")
  ) {
    const saved = window.localStorage.getItem(CHECKOUT_INTENT_PATHNAME_KEY);
    if (saved) {
      window.localStorage.removeItem(CHECKOUT_INTENT_PATHNAME_KEY);
      return saved;
    }
  }
  return p || "/";
}

/** Matches `CartSummaryLayout` Helmet `<title>Your cart</title>` when URL alone is ambiguous. */
function isCartPageDocumentTitle(): boolean {
  if (typeof document === "undefined") return false;
  return /\byour cart\b/i.test(document.title);
}

export const CartProvider = ({ children }: CartProviderProps): JSX.Element => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const qCartId = queryParams.get("cartId");
  const emailParam = queryParams.get("email");
  const { queryID } = (location.state as { queryID?: string }) || {};
  const [state, dispatch] = useReducer(
    process.env.GATSBY_VM_ENV !== "production"
      ? contextLogger(reducer)
      : reducer,
    initialCartState
  );
  const [store, setStore] = useState<CartStorage>(undefined);
  const [addItemQueue, setAddItemQueue] = useState<
    Array<{
      cartItemId?: string;
      offer?: VinoDataOffer;
      customerId?: number;
    }>
  >([]);

  const preloadedCheckoutUrlPromise = useRef<Promise<any> | null>(null);
  const preloadedCheckoutUrlTimestamp = useRef<number>(0);
  const preloadedCheckoutCartId = useRef<string | null>(null);

  const clearPreloadedUrl = () => {
    preloadedCheckoutUrlPromise.current = null;
    preloadedCheckoutUrlTimestamp.current = 0;
    preloadedCheckoutCartId.current = null;
  };
  const { updateTagsOnCartChange } = useOneSignal();
  const {
    user,
    isAuthenticated,
    getAccessToken,
    getTokenID,
    isLoading: isAuthenticating,
  } = useAuth();
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const customer_bigcommerce_id =
    userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];
  const { isMobile } = useScreenSize();

  const setBigCommerceId = async (): Promise<any> => {
    if (!customer_bigcommerce_id && user?.email) {
      // This is because the auth0 functions are still creating the customer in the background. So get it straight from BigCommerce
      try {
        const bcCustomer = await vinoFetch.get(
          "/api/get-customer-id-by-email",
          {
            params: {
              email: user.email,
              first_name: user.given_name,
              last_name: user.family_name,
            },
          }
        );
        return bcCustomer?.data[0]?.id;
      } catch (error) {
        return 0;
      }
    } else {
      return customer_bigcommerce_id;
    }
  };

  const getGeoLocation = async (): Promise<any> => {
    return vinoFetch.get(
      `${process.env.GATSBY_VINO_EDGE_FUNCTIONS_HOST}/edge/geolocation`
    );
  };

  const cartResolver = CartResolver;

  const fetchStore = async () => {
    let newStore;

    if (isAuthenticated) {
      const accessToken = await getAccessToken();
      const tokenID = await getTokenID();
      newStore = Auth0CartStorage(accessToken, tokenID);
    } else if (isAuthenticating || state?.isMerging) {
      newStore = undefined;
    } else {
      newStore = LocalCartStorage(
        BIG_COMMERCE_CART_ID_COOKIE,
        GUEST_CART_EMAIL_COOKIE
      );
    }

    setStore(newStore);
  };

  const handleCartMerging = async () => {
    dispatch({ type: "MERGE_CART_STARTED", error: null });

    const cookieCartId = await LocalCartStorage(
      BIG_COMMERCE_CART_ID_COOKIE
    ).fetchCartId();

    const storeCartId = await store.fetchCartId();

    let updatedCartId: string | undefined;
    let redirectToCheckout = false;
    let mergedCart: Cart | undefined;

    if (
      isAuthenticated &&
      cookieCartId &&
      storeCartId &&
      cookieCartId !== storeCartId
    ) {
      const {
        cart: dormantCart,
        error: dormantCartError,
      } = await cartResolver.loadCart({
        cartId: storeCartId,
        customerId: customer_bigcommerce_id ? customer_bigcommerce_id : 0,
        geoLocRegion: state.geoLocRegion,
      });

      if (dormantCart?.withSoldOutItem) {
        const soldOutProducts = dormantCart.lineItems.filter(
          (item) => item.isSoldOut
        );
        dispatch({
          type: "UPDATE_SOLD_OUT_PRODUCTS",
          soldOutProducts: soldOutProducts,
        });

        const promiseAll = soldOutProducts.map(async (item) => {
          const { cart } = await cartResolver.removeCartItem({
            cartId: storeCartId,
            cartItemId: item.id,
            geoLocRegion: state.geoLocRegion,
          });
          return cart;
        });
        await Promise.all(promiseAll).then((res) => {
          console.log(
            "ALL SOLD-OUT PRODUCTS HAVE BEEN REMOVED FROM THE CART.",
            res
          );
        });
      }

      dispatch({ type: "CREATE_CART_STARTED", error: null });
      const cartIds = dormantCart
        ? [storeCartId, cookieCartId]
        : [cookieCartId];
      const { cart, error } = await cartResolver.mergeCarts({
        cartIds,
        undefined,
        geoLocRegion: state.geoLocRegion,
      });

      if (error) {
        dispatch({ type: "MERGE_CART_COMPLETED" });
        return;
      }

      updatedCartId = cart.id;
      mergedCart = cart;
      // Redirect user straight to checkout when dormant cart is invalid
      if (localStorage.getItem("redirect_post_login_success") === "true" || !dormantCart) {
        redirectToCheckout = true;
      }

      dispatch({
        type: "CREATE_CART_COMPLETED",
        cart,
        isCartOpen: true,
        changeCartOnMerge: true,
        fetchingCart: true,
      });

      if (state.isCartOpen === false)
        trackEvent("Cart Dropdown Viewed", {
          action: "product added",
          device_type: isMobile ? "mobile" : "desktop",
        });

      // 2023-10-06: Commented out deletion of the cart to eliminate 500 errors and since we update the latest cartId anyway.
      // cartResolver.destroyCart({ cartId: cookieCartId });
      // cartResolver.destroyCart({ cartId: storeCartId });
    } else {
      updatedCartId = storeCartId || cookieCartId;

      redirectToCheckout = localStorage.getItem("redirect_post_login_success") === "true";

      // Merge cart ID from url queary parameter and the cookieCartId for guest users
      if (qCartId && !isAuthenticated) {
        let responseCart;
        let responseError;
        const { cart: checkCart } = await cartResolver.checkCart({
          cartId: qCartId,
          geoLocRegion: state.geoLocRegion,
        });

        if (checkCart && cookieCartId !== qCartId) {
          const cartIds = [cookieCartId];
          const { cart, error } = await cartResolver.mergeCarts({
            cartIds,
            cartId: qCartId,
            geoLocRegion: state.geoLocRegion,
          });

          responseError = error;
          responseCart = cart;

          if (responseError) {
            dispatch({ type: "MERGE_CART_COMPLETED" });
            return;
          }

          updatedCartId = responseCart.id;
          // 2023-10-06: Commented out deletion of the cart to eliminate 500 errors and since we update the latest cartId anyway.
          // await cartResolver.destroyCart({ cartId: cookieCartId });
        }
        // Remove the cart ID from the URL
        const updatedUrl = window.location.href.split("?")[0];
        window.history.replaceState({}, document.title, updatedUrl);
      }
    }

    // Only update the cart if it's changing
    if (updatedCartId && updatedCartId !== storeCartId) {
      clearPreloadedUrl();
      await store.setCartId(updatedCartId);
      if (redirectToCheckout) {
        await handleRedirectToCheckout({
          instant: true,
          checkoutIntentCart: mergedCart ?? state.cart,
        });
      }
    } else if (redirectToCheckout && updatedCartId) {
      // e.g. mini-cart password login: guest + logged-in cart ids already match — no id change, but user still wants checkout
      await handleRedirectToCheckout({
        instant: true,
        checkoutIntentCart: state.cart,
      });
    }

    // Clear Cookie store if we're now logged in
    if (isAuthenticated) {
      const _store = LocalCartStorage(
        BIG_COMMERCE_CART_ID_COOKIE,
        GUEST_CART_EMAIL_COOKIE
      );
      _store.removeCartId();
      if (_store.removeGuestCartEmail) _store.removeGuestCartEmail();
    }

    dispatch({ type: "MERGE_CART_COMPLETED" });
  };

  const processAddItemsQueue = async () => {
    if (!store || addItemQueue.length === 0) return;

    // Batch rapid clicks by grouping identical items
    const nextItem = addItemQueue[0];
    const matchingItems = addItemQueue.filter((item: any) => item.cartItemId === nextItem.cartItemId);
    const remainingQueue = addItemQueue.filter((item: any) => item.cartItemId !== nextItem.cartItemId);
    
    setAddItemQueue(remainingQueue);

    try {
      // In a real batch, we'd pass quantity: matchingItems.length, but since handleAddItem
      // doesn't support quantity yet, we just trigger them in Promise.all to parallelise.
      await Promise.all(matchingItems.map((item: any) => 
        handleAddItem(item.cartItemId, item.offer, item.customerId)
      ));
    } catch {
      setAddItemQueue((prev) => [...matchingItems, ...prev]);
    }
  };

  const waitForStore = async (): Promise<{ error?: Error }> => {
    let error: Error;
    let retries = 0;
    while (!store && retries < MAX_FETCH_STORE_RETRIES) {
      await new Promise((resolve) => {
        retries++;
        return setTimeout(resolve, 300);
      });
    }

    // Throw if max retries exceeded and still no store
    if (!store) error = new Error("Could not load cart storage provider");

    return { error };
  };

  const loadCart = async () => {
    dispatch({ type: "FETCH_CART_STARTED", error: null });

    const cartId = await store.fetchCartId();
    if (!cartId) {
      return dispatch({
        type: "FETCH_CART_COMPLETED",
        cart: null,
      });
    }

    const cust_id = await setBigCommerceId();

    const { cart, error } = await cartResolver.loadCart({
      cartId,
      customerId: cust_id ? cust_id : 0,
      geoLocRegion: state.geoLocRegion,
    });

    if (!cart) {
      await store.removeCartId();
      if (store.removeGuestCartEmail) await store.removeGuestCartEmail();
    }

    if (error) {
      dispatch({ type: "ERROR", error: error });
      return;
    }
    dispatch({ type: "FETCH_CART_COMPLETED", cart });
  };

  const setCartStateEmail = async (email?: string) => {
    if (emailParam && email) {
      const _store =
        store ||
        LocalCartStorage(BIG_COMMERCE_CART_ID_COOKIE, GUEST_CART_EMAIL_COOKIE);

      if (email) {
        await _store.setGuestCartEmail(email);
        return dispatch({ type: "UPDATE_CART_EMAIL", email });
      }

      const cookieCartEmail = await _store.getGuestCartEmail();
      if (emailParam && emailParam !== state.cart?.customerEmail) {
        await _store.setGuestCartEmail(emailParam);
        dispatch({ type: "UPDATE_CART_EMAIL", email: emailParam });
      } else if (cookieCartEmail) {
        dispatch({ type: "UPDATE_CART_EMAIL", email: cookieCartEmail });
      }
    }
  };

  const inferCheckoutSource = (pathnameForSource?: string): "mini_cart" | "cart_page" => {
    const raw =
      pathnameForSource ??
      location.pathname?.replace(/\/$/, "") ??
      "";
    const last = raw.split("/").filter(Boolean).pop() ?? "";
    if (last === "cart") return "cart_page";
    if (isCartPageDocumentTitle()) return "cart_page";
    return "mini_cart";
  };

  const handleRedirectToCheckout = async ({
    email,
    instant = false,
    preload = false,
    checkoutIntentCart,
  }: {
    email?: string | null;
    instant?: boolean;
    preload?: boolean;
    checkoutIntentCart?: Cart;
  }) => {
    const cartForTracking = checkoutIntentCart ?? state.cart;

    if (!preload && cartForTracking) {
      const trackingPath = resolveCheckoutTrackingLocation(location?.pathname);
      trackCheckoutButtonClicked({
        cart: cartForTracking,
        source: inferCheckoutSource(trackingPath),
        location: trackingPath,
        device_type: isMobile ? "mobile" : "desktop",
        customer_group: user?.customer_group?.name || "No Group",
        guestCheckout: isAuthenticated ? false : undefined,
      });
    }

    if (!preload) {
      dispatch({ type: "HANDLE_REDIRECT_TO_CHECKOUT_STARTED", error: null });
    }

    const currentCartId = state?.cart?.id ?? (store ? await store.fetchCartId() : undefined);
    const isFresh = Date.now() - preloadedCheckoutUrlTimestamp.current < 15 * 1000;
    const isCorrectCart = preloadedCheckoutCartId.current === currentCartId;

    if (preloadedCheckoutUrlPromise.current) {
      if (preload) {
        if (isFresh && isCorrectCart) return; // Already preloaded and fresh for this cart
      } else if (isFresh && isCorrectCart) {
        const { checkoutUrl, error } = await preloadedCheckoutUrlPromise.current;
        clearPreloadedUrl(); // consume it

        if (error) {
          dispatch({ type: "ERROR", error });
          return;
        }

        dispatch({
          type: "HANDLE_REDIRECT_TO_CHECKOUT_COMPLETED",
          isRedirecting: true,
        });

        window.location.assign(checkoutUrl);
        return;
      }
    }

    if (state.cart && !preload) {
      localStorage.setItem(
        "isGuest",
        state.cart.customerId === 0 ? "yes" : "no"
      );
    }

    const accessTokenPromise = isAuthenticated ? getAccessToken() : Promise.resolve(undefined);
    const cartId = await store.fetchCartId();

    if (email && !preload) {
      const updateEmail = await CartResolver.updateGuestCartEmail({
        email,
        cartId,
      });

      if (updateEmail.error) {
        dispatch({ type: "ERROR", error: updateEmail.error });
        return;
      }
    }

    if (!instant && !preload) {
      const cust_id = await setBigCommerceId();
      const { cart } = await cartResolver.loadCart({
        cartId,
        customerId: cust_id ? cust_id : 0,
        geoLocRegion: state.geoLocRegion,
      });

      if (cart?.customerId === 0 && store.removeGuestCartEmail) {
        await store.removeGuestCartEmail();
      }
    }

    if (!preload) {
      localStorage.setItem("redirect_post_login_success", "false");
    }

    const promise = (async () => {
      const accessToken = await accessTokenPromise;
      return cartResolver.loadCheckoutUrl({
        userId: user?.sub,
        accessToken,
        email,
        cartId,
      });
    })();

    if (preload) {
      preloadedCheckoutUrlPromise.current = promise;
      preloadedCheckoutUrlTimestamp.current = Date.now();
      preloadedCheckoutCartId.current = cartId;
      return;
    }

    const { checkoutUrl, error } = await promise;

    if (error) {
      dispatch({ type: "ERROR", error });
      return;
    }

    dispatch({
      type: "HANDLE_REDIRECT_TO_CHECKOUT_COMPLETED",
      isRedirecting: true,
    });

    window.location.assign(checkoutUrl);
  };

  const handleUpdateItem = async ({ cartItemId, productId, quantity }) => {
    if (!state?.cart || !state?.cart?.id) return;

    clearPreloadedUrl();
    
    const cartId = state.cart.id;
    const itemToUpdate = state.cart.lineItems.find((i: any) => i.id === cartItemId);
    const originalQuantity = itemToUpdate?.quantity || quantity;

    if (itemToUpdate) {
      dispatch({ type: "UPDATE_ITEM_OPTIMISTIC", payload: { cartItemId, productId, quantity, originalQuantity } });
    } else {
      dispatch({ type: "UPDATE_ITEM_STARTED", error: null });
    }

    const { cart, error } = await cartResolver.updateCartItem({
      cartId,
      cartItemId,
      productId,
      quantity,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      if (itemToUpdate) {
        dispatch({ type: "UPDATE_ITEM_ROLLBACK", payload: { cartItemId, originalQuantity } });
      }
      dispatch({
        type: "ERROR",
        error: error,
      });
      return;
    }

    dispatch({ type: "UPDATE_ITEM_COMPLETED", cart });
  };

  const handleRemoveItem = async (cartItemId: string) => {
    if (!state?.cart || !state?.cart?.id) return;

    clearPreloadedUrl();

    const cartId = state.cart.id;
    const itemToRemove = state.cart.lineItems.find((i: any) => i.id === cartItemId);
    
    if (itemToRemove) {
      dispatch({ type: "REMOVE_ITEM_OPTIMISTIC", payload: { cartItemId, savedItem: itemToRemove } });
      openToast(<SuccessToast message="Item removed from your cart." />);
    } else {
      dispatch({ type: "REMOVE_ITEM_STARTED", error: null });
    }

    if (!cartId) {
      dispatch({
        type: "REMOVE_ITEM_COMPLETED",
        cart: null,
        isCartOpen: false,
      });
      return;
    }

    const { cart, error } = await cartResolver.removeCartItem({
      cartId,
      cartItemId,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      if (itemToRemove) {
        dispatch({ type: "REMOVE_ITEM_ROLLBACK", payload: { savedItem: itemToRemove } });
      }
      
      dispatch({ type: "ERROR", error: error });
      const errorMsg = error?.response?.data?.title || error?.message || "Something went wrong removing the item.";
      openToast(<ErrorToast label="Uh-oh!" message={errorMsg} />);
      return;
    }

    updateTagsOnCartChange(cart);

    if (!cart) {
      dispatch({ type: "REMOVE_ITEM_COMPLETED", cart, isCartOpen: false });

      await store.removeCartId();
      if (store.removeGuestCartEmail) await store.removeGuestCartEmail();
      if (typeof window !== "undefined" && localStorage.getItem("_miniCartEmail"))
        localStorage.removeItem("_miniCartEmail");

      return;
    }

    dispatch({ type: "REMOVE_ITEM_COMPLETED", cart });
  };

  const handleCreateCart = async ({
    customerId,
    cartItemId,
    offer,
    tempId,
  }: {
    customerId?: number;
    cartItemId: string;
    offer?: VinoDataOffer;
    tempId?: string;
  }) => {
    clearPreloadedUrl();
    
    // Open the cart immediately so the user sees the optimistic UI before the API finishes
    dispatch({ type: "UPDATE_CART_OPEN", isCartOpen: true });
    
    dispatch({ type: "CREATE_CART_STARTED", error: null });
    const { cart, error } = await cartResolver.createCart({
      customerId,
      productId: cartItemId,
      offer,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      if (tempId) dispatch({ type: "ADD_ITEM_ROLLBACK", payload: { cartItemId, tempId } });
      trackOfferDetailsEvent("Product Added - Ineligible", {
        ...offer,
        customer_group: user?.customer_group?.name || "No Group",
      });
      dispatch({ type: "ERROR", error: error });
      
      const errorMsg = error?.response?.data?.title || error?.message || "Something went wrong. Please try again.";
      openToast(<ErrorToast label="Uh-oh!" message={errorMsg} />);
      return;
    }

    const { error: fetchStoreError } = await waitForStore();
    if (fetchStoreError) {
      if (tempId) dispatch({ type: "ADD_ITEM_ROLLBACK", payload: { cartItemId, tempId } });
      dispatch({ type: "ERROR", error: fetchStoreError });
      openToast(<ErrorToast label="Uh-oh!" message="Failed to load cart storage." />);
      return;
    }

    await store.setCartId(cart.id);

    dispatch({
      type: "CREATE_CART_COMPLETED",
      cart,
      isCartOpen: true,
    });

    if (state.isCartOpen === false)
      trackEvent("Cart Dropdown Viewed", {
        action: "product added",
        device_type: isMobile ? "mobile" : "desktop",
      });

    updateTagsOnCartChange(cart);
  };

  const handleAddItem = async (
    cartItemId?: string,
    offer?: VinoDataOffer,
    customerId?: number,
    buttonPosition?: ButtonPosition
  ) => {
    if (!store || state.isLoading) {
      const itemToBeAdded = { cartItemId, offer, customerId };
      setAddItemQueue((prev) => [...prev, itemToBeAdded]);
      return;
    }

    clearPreloadedUrl();
    
    const isGCOffer = offer?.sku?.code?.includes("GIFT");
    const activeCoupon = state.cart?.coupons && state.cart?.coupons[0];

    // Pre-empt Gift Card vs Coupon conflict
    if (isGCOffer && activeCoupon) {
      const customError = {
        name: "Error",
        message: config.GC_TO_GC_ERROR || "Cannot combine Gift Cards and Coupons",
        stack: "coupon",
        isHidden: false,
      };
      dispatch({ type: "ERROR", error: customError });
      return;
    }

    const tempId = Math.random().toString(36).substring(7);
    dispatch({ type: "ADD_ITEM_OPTIMISTIC", payload: { cartItemId, offer, tempId } });
    
    // Optimistically show success toast instantly
    openToast(<SuccessToast message="Item added to your cart!" />);

    // Parallelise local async calls
    const [cartId, accessToken] = await Promise.all([
      store.fetchCartId(),
      isAuthenticated ? getAccessToken() : Promise.resolve(undefined)
    ]);

    const { cart, error } = await cartResolver.addCartItem({
      customerId: customerId ? customerId : undefined,
      cartId,
      cartItemId,
      accessToken,
      offer,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      dispatch({ type: "ADD_ITEM_ROLLBACK", payload: { cartItemId, tempId } });
      trackOfferDetailsEvent("Product Added - Ineligible", {
        ...offer,
        customer_group: user?.customer_group?.name || "No Group",
      });
      dispatch({ type: "ERROR", error: error });
      
      const errorMsg = error?.response?.data?.title || error?.message || "Something went wrong. Please try again.";
      openToast(<ErrorToast label="Uh-oh!" message={errorMsg} />);
      return;
    }

    if (cart) {
      dispatch({ type: "ADD_ITEM_COMPLETED", cart });
      updateTagsOnCartChange(cart);
    } else {
      handleCreateCart({
        customerId: customerId ? customerId : undefined,
        cartItemId: cartItemId,
        offer,
        tempId,
      });
    }

    const cartItem = cart?.lineItems.find(
      (i) =>
        i.productId ===
          parseInt(offer.productId?.toString() || offer.bigcommerceId) &&
        i.name === offer.name &&
        i.quantity > 1
    );

    if (cartItem) {
      trackOfferDetailsEvent(
        "Product Quantity Increased",
        {
          eventType: EventType.Conversion,
          eventSubType: EventSubType.AddToCart,
          cartId: cartId,
          name: cartItem.name,
          productId: cartItem.productId,
          quantity: cartItem.quantity,
          new_quantity: cartItem.quantity + 1,
          SKU: cartItem.sku,
          changed_by: 1,
          imageUrl: cartItem.imageUrl,
          url: `${location.origin}/wines${cartItem.slug}`,
          customer_group: user?.customer_group?.name || "No Group",
          tiersEligible: offer?.tiersEligible,
        },
        buttonPosition,
        offer?.queryID || queryID
      );
    } else {
      trackOfferDetailsEvent(
        "Product Added",
        {
          eventType: EventType.Conversion,
          eventSubType: EventSubType.AddToCart,
          ...offer,
          cartId: cartId,
          customer_group: user?.customer_group?.name || "No Group",
          tiersEligible: offer?.tiersEligible,
        },
        buttonPosition,
        offer?.queryID || queryID
      );
    }
  };

  const setCartIsOpen = (visible: boolean) => {
    if (visible !== state?.isCartOpen)
      dispatch({ type: "UPDATE_CART_OPEN", isCartOpen: visible, error: null });
  };

  const setGeoLocRegion = (region: string) => {
    dispatch({ type: "SET_GEO_LOC_REGION", region });
  };

  const checkAvailability = async (
    cartItemId: number | string,
    offer: Offer
  ) => {
    try {
      const response = await axios.get(
        `/api/inventory-levels?ids=${cartItemId}`
      );
      const match = response.data.find((inv) => inv.id == cartItemId);
      const matchInCart = state?.cart?.lineItems?.find(
        (item) => item.productId == cartItemId
      );
      const buyingQuantity = matchInCart ? matchInCart.quantity + 1 : 1;

      if (!match || match.inventory_level < buyingQuantity) {
        openToast(
          <ErrorToast
            label="Refreshing your cart..."
            message={`Apologies, but the "${offer.name}" has sold out!`}
          />
        );
        return false;
      } else {
        if (!state.cart || state.cart?.lineItems?.length < 1) {
          return true;
        }

        if (match.inventory_level < LOW_INVENTORY_COUNT) {
          openToast(
            <SuccessToast
              label="Hurry!"
              message="Selling FAST! Don't miss out!"
            />
          );
        } else {
          openToast(<SuccessToast message="Item added to your cart!" />);
        }
        return true;
      }
    } catch (e) {
      console.error(e);
      openToast(
        <ErrorToast
          label="Something went wrong..."
          message="Failed to check inventory, please try later."
        />
      );
      return false;
    }
  };

  const checkPurchaseQuantityLimits = async (
    cartItemId: number | string
  ): Promise<boolean> => {
    try {
      const response = await axios.get(`/api/product-details?id=${cartItemId}`);
      const product = response.data;

      const matchInCart = state?.cart?.lineItems?.find(
        (item) => item.productId == cartItemId
      );
      const buyingQuantity = matchInCart ? matchInCart.quantity + 1 : 1;

      const maxQty = product?.order_quantity_maximum ?? Infinity;

      if (maxQty > 0 && buyingQuantity > maxQty) {
        openToast(
          <ErrorToast
            label="Uh-oh!"
            message={`There's a limit of ${maxQty} per customer for this offer!`}
          />
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error fetching product details:", error);
      openToast(
        <ErrorToast
          label="Uh-oh!"
          message="Unable to verify purchase limits. Please try again later."
        />
      );
      return false;
    }
  };

  const addItem = async (
    cartItemId: string,
    offer?: VinoDataOffer,
    customerId?: number,
    buttonPosition?: ButtonPosition,
    setLoadingStatus?: Function
  ) => {
    // Fire the optimistic update and backend mutation
    await handleAddItem(cartItemId, offer, customerId, buttonPosition);
    
    // Disable the button spinner only AFTER the background network request finishes
    if (setLoadingStatus) setLoadingStatus();
  };

  const removeItem = useCallback(
    (cartItemId): Promise<void> => {
      return handleRemoveItem(cartItemId);
    },
    [store, state]
  );

  const updateItem = useCallback(
    (args): Promise<void> => {
      return handleUpdateItem(args);
    },
    [store, state]
  );

  const initialLoad = async () => {
    await handleCartMerging();
    if (!state.isRedirecting) await loadCart();
    if (typeof window !== "undefined") {
      const isCartPage = location && location.pathname.includes("cart");
      const urlParams = new URLSearchParams(window.location.search);
      const showMiniCart = urlParams.get("show_mini_cart");
      if (showMiniCart === "true" && !isCartPage) {
        setCartIsOpen?.(true);
      }
    }
  };

  const handleAddCoupon = async (
    couponCode?: string,
    behindTheScene?: boolean
  ) => {
    if (
      !store ||
      !state?.cart ||
      !state?.cart?.id ||
      state.isLoading ||
      !couponCode
    )
      return;

    clearPreloadedUrl();
    dispatch({ type: "ADD_COUPON_STARTED", error: null, behindTheScene });

    if (state.cart.cartTotal < config.MINIMUM_VOUCHER_REQUIREMENT) {
      const customError = {
        name: "Error",
        message: config.MINIMUM_VOUCHER_ERROR_MESSAGE,
        stack: "coupon",
        isHidden: behindTheScene,
      };
      trackCartCouponEvent(
        "Coupon Applied",
        location.pathname,
        state.cart,
        couponCode,
        null,
        customError.message
      );
      dispatch({ type: "ERROR", error: customError });
      return customError;
    }

    const cartId = state.cart.id; // await store.fetchCartId();
    const accessToken = isAuthenticated ? await getAccessToken() : undefined;

    const { withGiftCard } = state.cart;

    if (withGiftCard) {
      const customError = {
        name: "Error",
        message: config.GC_TO_GC_ERROR,
        stack: "coupon",
        isHidden: behindTheScene,
      };
      trackCartCouponEvent(
        "Coupon Applied",
        location.pathname,
        state.cart,
        couponCode,
        null,
        customError.message
      );
      dispatch({ type: "ERROR", error: customError });
      return config.GC_TO_GC_ERROR;
    }

    const promotionByCode = couponCode
      ? await CartResolver.getPromotionByCode(couponCode)
      : null;

    if (
      state.cart &&
      parseInt(state.cart.customerId) === 0 &&
      promotionByCode &&
      promotionByCode.length > 0
    ) {
      const promotionItem = promotionByCode[0];
      if (isCouponForLoggedInUser(promotionItem)) {
        const guestUserErr =
          "Hmmm. You need to be signed-in to try this coupon. Why not join/login and try again?";

        const customError = {
          name: "Error",
          message: guestUserErr,
          stack: "guest-coupon",
          isHidden: behindTheScene,
        };
        trackCartCouponEvent(
          "Coupon Applied",
          location.pathname,
          state.cart,
          couponCode,
          null,
          customError.message
        );
        dispatch({ type: "ERROR", error: customError });
        return guestUserErr;
      }
    }

    if (
      parseInt(customer_bigcommerce_id) !== 0 &&
      promotionByCode &&
      promotionByCode[0]?.name === `Referral_code_${customer_bigcommerce_id}`
    ) {
      const selfReferralErr = "You cannot refer yourself.";

      const customError = {
        name: "Error",
        message: selfReferralErr,
        stack: "coupon",
        isHidden: behindTheScene,
      };
      trackCartCouponEvent(
        "Coupon Applied",
        location.pathname,
        state.cart,
        couponCode,
        null,
        customError.message
      );
      dispatch({ type: "ERROR", error: customError });
      return selfReferralErr;
    }

    const { cart, error } = await cartResolver.addCoupon({
      cartId,
      couponCode,
      accessToken,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      const errorData = error?.response?.data;
      let errMsg = errorData.title?.replace(/(<([^>]+)>)/gi, "");
      if (
        errMsg === "You are not in the customer group the promotion targets"
      ) {
        errMsg = config.INVALID_COUPON_ELIGIBILITY_MESSAGE;
      } else if (
        couponCode &&
        errMsg === `The coupon code ${couponCode} you entered is not valid.`
      ) {
        errMsg = config.INVALID_COUPON_MESSAGE.replace("<code>", couponCode);
      } else if (errMsg.indexOf(`The coupon code you entered expired`) > -1) {
        errMsg = config.EXPIRED_COUPON_MESSAGE;
      } else {
        errMsg = config.INVALID_COUPON_MESSAGE.replace("<code>", couponCode);
      }

      const customError = {
        name: "Error",
        message: errMsg || config.INVALID_COUPON_MESSAGE,
        stack: "coupon",
        isHidden: behindTheScene,
      };
      trackCartCouponEvent(
        "Coupon Applied",
        location.pathname,
        state.cart,
        couponCode,
        null,
        customError.message
      );
      dispatch({ type: "ERROR", error: customError });
      return error;
    }

    if (cart) {
      dispatch({ type: "ADD_COUPON_COMPLETED", cart });
      return cart;
    }
  };

  const handleRemoveCoupon = async (couponCode: string) => {
    if (!state?.cart || !state?.cart?.id) return;

    localStorage.removeItem("vmcode");

    clearPreloadedUrl();
    dispatch({ type: "REMOVE_COUPON_STARTED", error: null });

    const cartId = state.cart.id; // await store.fetchCartId();

    const { cart, error } = await cartResolver.removeCoupon({
      cartId,
      couponCode,
      geoLocRegion: state.geoLocRegion,
    });

    if (error) {
      const errorData = error?.response?.data;
      let errMsg = errorData.title?.replace(/(<([^>]+)>)/gi, "");
      if (
        couponCode &&
        errMsg === `The coupon code ${couponCode} you entered is not valid.`
      ) {
        errMsg = config.INVALID_COUPON_MESSAGE.replace("<code>", couponCode);
      } else if (errMsg.indexOf(`The coupon code you entered expired`) > -1) {
        errMsg = config.EXPIRED_COUPON_MESSAGE;
      } else {
        errMsg = config.INVALID_COUPON_MESSAGE.replace("<code>", couponCode);
      }

      const customError = {
        name: "Error",
        message: errMsg,
        stack: "coupon",
      };
      dispatch({ type: "ERROR", error: customError });
      return;
    }

    updateTagsOnCartChange(cart);

    if (!cart) {
      dispatch({ type: "REMOVE_COUPON_COMPLETED", cart, isCartOpen: false });

      await store.removeCartId();
      if (store.removeGuestCartEmail) await store.removeGuestCartEmail();
      return;
    }

    dispatch({ type: "REMOVE_COUPON_COMPLETED", cart });
  };

  const addCoupon = useCallback(
    (couponCode, behindTheScene = false): Promise<void> => {
      return handleAddCoupon(couponCode, behindTheScene);
    },
    [store, state]
  );

  const removeCoupon = useCallback(
    (couponCode): Promise<void> => {
      return handleRemoveCoupon(couponCode);
    },
    [store, state]
  );

  const updateSoldOutProductList = () => {
    dispatch({ type: "UPDATE_SOLD_OUT_PRODUCTS", soldOutProducts: [] });
  };

  const ctx: CartContextInterface = useMemo(
    () => ({
      ...state,
      addItem,
      updateItem,
      removeItem,
      setCartIsOpen,
      addCoupon,
      removeCoupon,
      handleRedirectToCheckout,
      setCartStateEmail,
      setGeoLocRegion,
      updateSoldOutProductList,
    }),
    [
      state,
      addItem,
      removeItem,
      updateItem,
      setCartIsOpen,
      addCoupon,
      removeCoupon,
      setGeoLocRegion,
      updateSoldOutProductList,
      store,
    ]
  );

  const fetchGeoLocation = async () => {
    const geoLocation = await getGeoLocation();
    if (geoLocation?.data?.geo) {
      await setGeoLocRegion(geoLocation?.data?.geo?.subdivision?.code);
      localStorage.setItem(
        "vm-geolocation",
        JSON.stringify(geoLocation?.data?.geo)
      );
    }
  };

  useEffect(() => {
    if (state.error && state.isLoading) {
      delete state.error;
      dispatch({ ...state, type: "RESET_ERROR", error: null });
    }

    if (store && !state.isLoading) processAddItemsQueue();

    const applyVmcode = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const vmcodeFromUrl = queryParams.get("vmcode");
      const vmcodeFromStorage = localStorage.getItem("vmcode");
      const currentCoupon = state.cart?.coupons?.[0]?.code;

      const shouldApply =
        state &&
        !state.isSilentProcessing &&
        !state.error &&
        state.cart?.id &&
        ((vmcodeFromUrl && currentCoupon !== vmcodeFromUrl) ||
          (vmcodeFromStorage &&
            vmcodeFromStorage !== "undefined" &&
            currentCoupon !== vmcodeFromStorage));

      if (vmcodeFromUrl) {
        // Remove vmcode from URL without reloading the page
        queryParams.delete("vmcode");
        const newUrl = `${location.pathname}${
          queryParams.toString() ? `?${queryParams}` : ""
        }${location.hash}`;
        window.history.replaceState({}, "", newUrl);

        if (shouldApply) {
          // Apply vmcode from URL
          await addCoupon(vmcodeFromUrl, true);
          console.log("Applied vmcode from URL");
        }
        // Store vmcode from URL to localStorage
        localStorage.setItem("vmcode", vmcodeFromUrl);
      }

      if (
        vmcodeFromStorage &&
        vmcodeFromStorage !== "undefined" &&
        shouldApply
      ) {
        // Apply vmcode from localStorage
        await addCoupon(vmcodeFromStorage, true);
        console.log("Applied vmcode from localStorage");
      }
    };

    applyVmcode();
  }, [state, store]);

  useEffect(() => {
    if (store && state && !state.isLoading && !state.isMerging) {
      initialLoad();
    }
  }, [store]);

  useEffect(() => {
    if (!store) fetchStore();
    if (state.geoLocRegion === "") fetchGeoLocation();
  }, [isAuthenticating, isAuthenticated]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.body.classList.toggle("cart-open", state.isCartOpen);
    document.body.classList.toggle("cart-close", !state.isCartOpen);
  }, [state.isCartOpen]);

  return <CartContext.Provider value={ctx}>{children}</CartContext.Provider>;
};

export default CartProvider;
